import React from "react";
import styled from "styled-components";

import Header from "../../commons/Header";
import Footer from "../../commons/Footer";
import ProvideLandingSearch from "../../providers/ProvideLandingSearch";
import { LoggedInUserContext } from "../../providers/ProvideLoggedInUser";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import {
  outerContainerCss,
  narrowScreenMediaQuery,
  brand_orange_deep,
} from "../../constants/styles";
import {
  LearningVideoGridCard,
  LearningVideoGridSkeleton,
} from "../../homes/sections/VideoPembelajaranCarousel";
import useVideoPembelajaranIndex from "./useVideoPembelajaranIndex";
import MateriSidebar from "./MateriSidebar";
import FilterBar from "./FilterBar";
import Pager from "../../webinars/index/Pager";

// Cards and the pager are reused rather than re-implemented: the card comes from
// the homepage carousel (so the two surfaces cannot drift), and Pager is fully
// generic already.

const Page = styled.div`
  background: #ffffff;
  min-height: 100vh;
  color: #1f2937;
  font-family: "Montserrat", sans-serif;

  &[data-theme="dark"] {
    background: #0f172a;
    color: #f3f4f6;
  }
`;

const Main = styled.main`
  ${outerContainerCss}
  padding-top: 48px;
  padding-bottom: 80px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media ${narrowScreenMediaQuery} {
    padding-top: 32px;
  }
`;

const PageHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  font-size: 34px;
  font-weight: 800;
  color: #1f2937;
  margin: 0;
  letter-spacing: -0.01em;

  @media ${narrowScreenMediaQuery} {
    font-size: 26px;
  }

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 14px;
  max-width: 640px;
  margin: 0;
  line-height: 1.6;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

// Sidebar rail + results. Collapses to a single column on narrow, where
// MateriSidebar swaps its rail for a combobox.
const Layout = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 24px;
  align-items: start;

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Results = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const ResultMeta = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

// Subgrid host: each card spans 5 shared row tracks and inherits them via
// `grid-template-rows: subgrid`, so thumbnail, title, meta, chips and progress
// line up across every card in a row regardless of title length.
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  column-gap: 20px;
  row-gap: 20px;

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    column-gap: 16px;
    row-gap: 16px;
  }
`;

const EmptyState = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 16px;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);
    border-color: rgba(255, 255, 255, 0.14);
  }
`;

const InlineReset = styled.button`
  border: none;
  background: transparent;
  color: ${brand_orange_deep};
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  font-size: 14px;
`;

// Stable keys for the loading placeholders — index keys would churn on every
// perPage change and trip no-array-index-key.
const SKELETON_KEYS = Array.from(
  { length: 12 },
  (_, i) => `vp-skeleton-${i + 1}`,
);

export default function VideoPembelajaranIndexApp({
  theme = "light",
  learningNameOptions = [],
  categoryOptions = [],
}) {
  const resolved = theme === "dark" ? "dark" : "light";

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolved);
    return () => document.documentElement.removeAttribute("data-theme");
  }, [resolved]);

  const auth = React.use(LoggedInUserContext);
  const signedIn = Boolean(auth && auth.signedIn);

  const {
    filters,
    results,
    meta,
    loading,
    error,
    update,
    setPage,
    reset,
    activeFilterCount,
    perPage,
    commitSearch,
    searchResetToken,
  } = useVideoPembelajaranIndex();

  const totalAcrossGroups = React.useMemo(
    () => learningNameOptions.reduce((sum, o) => sum + (o.count || 0), 0),
    [learningNameOptions],
  );

  const activeGroup = React.useMemo(
    () =>
      learningNameOptions.find((o) => o.slug === filters.learning_name) || null,
    [learningNameOptions, filters.learning_name],
  );

  // Stable identity so the memoized sidebar does not re-render on every parent
  // render (results landing, loading flipping).
  const selectLearningName = React.useCallback(
    (slug) => update({ learning_name: slug }),
    [update],
  );

  // Memoized: 12 cards, each a subgrid of styled-components. Without this the
  // whole grid is rebuilt on any parent render.
  const body = React.useMemo(() => {
    if (error) {
      return (
        <EmptyState role="alert">
          Terjadi kesalahan saat memuat video. Silakan muat ulang halaman.
        </EmptyState>
      );
    }
    if (loading) {
      return (
        <Grid>
          {SKELETON_KEYS.slice(0, perPage).map((key) =>
            LearningVideoGridSkeleton(key),
          )}
        </Grid>
      );
    }
    if (results.length === 0) {
      return (
        <EmptyState role="status">
          Tidak ada video yang cocok dengan filter Anda.
          {activeFilterCount > 0 && (
            <>
              {" "}
              <InlineReset type="button" onClick={reset}>
                Reset filter
              </InlineReset>
            </>
          )}
        </EmptyState>
      );
    }
    return <Grid>{results.map((video) => LearningVideoGridCard(video))}</Grid>;
  }, [error, loading, results, perPage, activeFilterCount, reset]);

  const rangeStart = (meta.page - 1) * (meta.perpage || perPage) + 1;
  const rangeEnd = Math.min(
    meta.page * (meta.perpage || perPage),
    meta.total || 0,
  );

  return (
    <ProvideLandingSearch>
      <Page data-theme={resolved}>
        <Header />
        <Main>
          <PageHeader>
            <Title>Semua Video Pembelajaran</Title>
            <Subtitle>
              Kumpulan video materi belajar pajak, dikelompokkan per Nama Materi
              Belajar. Pilih materi di samping atau cari langsung video yang
              kamu butuhkan.
            </Subtitle>
          </PageHeader>

          <SectionErrorBoundary name="VideoPembelajaranFilters">
            <FilterBar
              filters={filters}
              update={update}
              reset={reset}
              activeFilterCount={activeFilterCount}
              signedIn={signedIn}
              categoryOptions={categoryOptions}
              commitSearch={commitSearch}
              searchResetToken={searchResetToken}
              loading={loading}
            />
          </SectionErrorBoundary>

          <Layout>
            <MateriSidebar
              options={learningNameOptions}
              value={filters.learning_name}
              onChange={selectLearningName}
              total={totalAcrossGroups}
            />

            <Results>
              <ResultMeta aria-live="polite">
                {loading
                  ? "Memuat video..."
                  : meta.total > 0
                    ? `Menampilkan ${rangeStart}-${rangeEnd} dari ${meta.total} video${
                        activeGroup ? ` di ${activeGroup.name}` : ""
                      }`
                    : "Tidak ada video"}
              </ResultMeta>

              {body}

              <Pager page={meta.page} pages={meta.pages} onChange={setPage} />
            </Results>
          </Layout>
        </Main>
        <Footer />
      </Page>
    </ProvideLandingSearch>
  );
}
